import { CheckCircleFilled, CloseCircleFilled, DeleteFilled, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Popconfirm, Select, Space, Table, message } from "antd";
import { ColumnType, TablePaginationConfig } from "antd/es/table";
import { useEffect, useState } from "react";
import { Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";

import StringUtils from "../../util/StringUtils";
import KeyCodeUtils from "../../util/KeyCodeUtils";
import GatewayApi from "../../api/GatewayApi";
import ProductApi from "../../api/ProductApi";
import PermissionWrapper from "../../components/permission/PermissionWrapper";
import GatewayDetails from "./components/gateway-details/GatewayDetails";
import { CreateGatewayWizard } from "./components/create-gateway-wizard/CreateGatewayWizard";
import { GatewayManagementWrapper, FilterItem, FilterItemLabel, TableWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper } from "./GatewayManagement.styled";

interface GatewayManagementProps {

}

interface TableParams {
    pagination?: TablePaginationConfig;
}

const GatewayManagement: React.FC<GatewayManagementProps> = (props)=> {
    // api
    let gatewayApi = new GatewayApi();
    let productApi = new ProductApi();
    let [messageApi, contextHolder] = message.useMessage();
    let navigate = useNavigate();
    
    // params
    let [searchParams] = useSearchParams();

    // state
    let [gatewayViews, setGatewayViews] = useState([]);
    let [openCreateGatewayModal, setOpenCreateGatewayModal] = useState(false);

    let [loading, setLoading] = useState(true);
    let [productModel, setProductModel] = useState(searchParams.get('productModel'));
    let [search, setSearch] = useState("");
    let [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
        },
    });

    let [productModelOptions, setProductModelOptions] = useState([]);
    
    // events
    let viewGatewayDetails = (gatewayView: any)=> {
        navigate(`./gateway/${gatewayView.gateway.id}`);
    };

    let onTableChange = (pagination)=> {
        tableParams.pagination = pagination;
        setTableParams({pagination: pagination});
        loadGatewayViews();
    };

    let onCreateGatewayClick = ()=> {
        setOpenCreateGatewayModal(true);
    };

    let onSearchKeyDown = (e)=> {
        if (e.keyCode == KeyCodeUtils.ENTER) {
            loadGatewayViews();
        }
    };

    let onDeleteGateway = async (gatewayView: any)=> {
        await gatewayApi.delete(gatewayView.gateway.id);
        messageApi.open({
            type: 'success',
            content: '删除网关成功',
          });
        loadGatewayViews();
    };

    // load gateway views
    let loadGatewayViews = async ()=> {
        // loading animation
        setLoading(true);
        let gatewayViewsPage = (await gatewayApi.queryViews({
            productModel: productModel,
            search: search,
            pageNo: tableParams.pagination.current,
            pageSize: tableParams.pagination.pageSize,
        })).data.data;

        let newTableParams = {pagination: {...tableParams.pagination}};
        newTableParams.pagination.total = gatewayViewsPage.totalElements;
        setTableParams(newTableParams);
        setGatewayViews(gatewayViewsPage.elements);
        setLoading(false);
    };
    
    let loadProductModels = async ()=> {
        let products = (await productApi.getAll()).data.data;
        let productModelOptions = products.map((e)=> {
            return {
                label: e.model,
                value: e.model,
            };
        });
        setProductModelOptions(productModelOptions);
    };

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '状态',
            dataIndex: 'gateway.status',
            key: "gateway.status",
            align: 'center',
            render: (text, record, index)=> {
                return record.gateway.status == 'ONLINE' 
                    ? <CheckCircleFilled style={{color: 'green'}} />
                    : <CloseCircleFilled style={{color: 'grey'}} />
            }
        },
        {
            title: ' 网关名称',
            dataIndex: 'gateway.name',
            key: 'gateway.name',
            align: 'center',
            render: (text, record, index)=> {
                return <a onClick={()=> viewGatewayDetails(record)}>{record.gateway.name}</a>
            }
        },
        {
            title: '产品型号',
            dataIndex: 'product.modelName',
            key: 'product.modelName',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.product?.modelName}</span>
            }
        },
        {
            title: '所属用户',
            dataIndex: 'tenant.name',
            key: 'tenant.name',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.tenant?.name)}</span>
            }
        },
        {
            title: '最后在线时间',
            dataIndex: 'node.lastSeenTime',
            key: 'node.lastSeenTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ moment(parseInt(record.node?.lastSeenTime)).format("YYYY-MM-DD HH:mm:ss") }</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> viewGatewayDetails(record)}>查看详情</a>
                    <PermissionWrapper permissionCodePath="gatewate-management.gatewate.delete-gateway">
                        <Popconfirm
                                title="删除网关"
                                description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>此操作将删除网关，确认要删除么?</div> }
                                onConfirm={()=> onDeleteGateway(record)}
                                okText="确定"
                                cancelText="取消"
                            > 
                            <a>删除网关</a>
                        </Popconfirm>
                    </PermissionWrapper>
                </Space>
            }
        }
    ];

    // effect
    useEffect(()=> {
        loadGatewayViews();
        loadProductModels();
    }, []);

    useEffect(()=> {
        loadGatewayViews();
    }, [productModel, search]);
    
    // render
    return (
        <GatewayManagementWrapper>
            {contextHolder}
            <ToolBarWrapper>
                <ToolBarLeftWrapper>
                    <Space size="middle">
                        <PermissionWrapper permissionCodePath="gateway-management.gateway.create-gateway">
                            <Button type="primary" icon={<PlusOutlined />} onClick={onCreateGatewayClick}>新增网关</Button>
                        </PermissionWrapper>
                    </Space>
                </ToolBarLeftWrapper>

                <ToolBarRightWrapper>
                    <Space size="middle">
                        <FilterItem>
                            <FilterItemLabel>网关型号</FilterItemLabel>
                            <Select
                                allowClear
                                maxTagCount={2}
                                style={{ width: '150px' }}
                                placeholder="请选择网关型号"
                                value={productModel}
                                onChange={(selectedValue)=> setProductModel(selectedValue)}
                                options={productModelOptions}
                            />
                        </FilterItem>
                        <FilterItem>
                            <FilterItemLabel>搜索</FilterItemLabel>
                            <Input 
                                placeholder="请输入关键字" 
                                suffix={<SearchOutlined />} 
                                size="middle" 
                                value={search} 
                                onChange={(e)=> setSearch(e.target.value)} 
                                onKeyDown={(e)=> onSearchKeyDown(e)}
                            />
                        </FilterItem>
                    </Space>
                </ToolBarRightWrapper>

                <Modal open={openCreateGatewayModal} 
                    onClose={()=> setOpenCreateGatewayModal(false)} 
                    footer={null} 
                    closable={false}
                    destroyOnClose={true}
                    width={1000}
                    height={600}
                    >
                    <CreateGatewayWizard onClose={()=> setOpenCreateGatewayModal(false)} onComplete={()=> loadGatewayViews()}/>
                </Modal>
            </ToolBarWrapper>
            <TableWrapper>
                <Table 
                    rowKey={(record)=> record.gateway.id}
                    columns={columns} 
                    dataSource={gatewayViews}
                    pagination={tableParams.pagination}
                    loading={loading}
                    onChange={onTableChange}
                />
            </TableWrapper>

            <Routes>
                <Route path="gateway/:gatewayId" element={<GatewayDetails onClose={()=>{}} onComplete={()=>{}}/>} />
            </Routes>
        </GatewayManagementWrapper>
    );
}

export default GatewayManagement;
